import { useEffect, useState } from "react";

/**
 * Estado de la instalación de la PWA (Corvana, Agua o Seguridad).
 *
 * El evento `beforeinstallprompt` lo captura index.js apenas carga la página
 * —mucho antes de que monte React— y lo deja en window.__promptInstalacion.
 * Acá se lee de ahí y se escucha "pwa:instalable" por si llega después.
 *
 *   prompt     el navegador ofrece instalar con un toque (Chrome, Edge, Android)
 *   instalada  ya está instalada en este aparato
 *   plataforma "ios" | "android" | "escritorio" — define qué pasos mostrar
 *              cuando no hay instalación de un toque (Safari no la tiene)
 *   navegador  solo en Android: con cuál está entrando (ver navegadorAndroid)
 *   instalar() abre el diálogo de instalación; devuelve true si aceptó
 */
export function plataformaActual() {
  const ua = navigator.userAgent || "";
  // iPadOS se presenta como Mac: se reconoce por la pantalla táctil.
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "escritorio";
}

/**
 * Con qué navegador está entrando, dentro de Android. La instalación no se
 * comporta igual en todos:
 *
 *   "chrome"   Chrome, Edge o Brave: es el camino que funciona siempre
 *   "samsung"  Samsung Internet: instala, pero en varios celulares Google Play
 *              Protect corta la instalación con "Se bloqueó la app no segura"
 *   "webview"  el navegador de adentro de WhatsApp, Facebook o Instagram: no
 *              instala nada, hay que abrir el link afuera
 *   "otro"     Firefox, Opera, Mi Browser…
 */
export function navegadorAndroid() {
  const ua = navigator.userAgent || "";
  // El orden importa: Samsung Internet y los navegadores de adentro de las
  // apps también dicen "Chrome/" en su user agent.
  if (/SamsungBrowser/i.test(ua)) return "samsung";
  if (/; wv\)|FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger/i.test(ua)) return "webview";
  if (/EdgA\//i.test(ua)) return "chrome";
  if (/Chrome\//i.test(ua) && !/OPR\/|UCBrowser|MiuiBrowser|HuaweiBrowser|YaBrowser/i.test(ua)) return "chrome";
  return "otro";
}

export function corriendoInstalada() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function useInstalarApp() {
  const [prompt, setPrompt] = useState(() => window.__promptInstalacion || null);
  const [instalada, setInstalada] = useState(false);

  useEffect(() => {
    function onInstalable() { setPrompt(window.__promptInstalacion || null); }
    function onInstalada() { setPrompt(null); setInstalada(true); }
    window.addEventListener("pwa:instalable", onInstalable);
    window.addEventListener("pwa:instalada", onInstalada);
    onInstalable();
    return () => {
      window.removeEventListener("pwa:instalable", onInstalable);
      window.removeEventListener("pwa:instalada", onInstalada);
    };
  }, []);

  // ¿Ya está instalada? Tres señales, de la más fiable a la más débil:
  //   1. Estamos corriendo dentro de la app instalada (display-mode standalone)
  //   2. getInstalledRelatedApps: Chrome sabe si la PWA está instalada aunque
  //      la estemos viendo desde una pestaña normal
  //   3. La marca que dejamos en localStorage cuando se instaló
  useEffect(() => {
    if (corriendoInstalada()) { setInstalada(true); return undefined; }
    try { if (localStorage.getItem("pwa_instalada") === "true") setInstalada(true); } catch { /* modo privado */ }
    let cancelado = false;
    navigator.getInstalledRelatedApps?.()
      .then((apps) => { if (!cancelado && apps.length > 0) setInstalada(true); })
      .catch(() => { /* navegador sin soporte: quedan las otras dos señales */ });
    return () => { cancelado = true; };
  }, []);

  async function instalar() {
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__promptInstalacion = null;
    setPrompt(null);
    if (outcome === "accepted") {
      try { localStorage.setItem("pwa_instalada", "true"); } catch { /* modo privado */ }
      setInstalada(true);
      return true;
    }
    return false;
  }

  const plataforma = plataformaActual();
  return { prompt, instalada, plataforma, instalar, navegador: plataforma === "android" ? navegadorAndroid() : null };
}
